import { useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ArrowUpRight, FileText, ExternalLink } from "lucide-react";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { resolveUrl } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const linkify = (text) => (text || "").split(/(https?:\/\/\S+|(?<=^|\s)\/[a-z0-9-]+(?=[\s.,)]|$))/g).map((part, i) => {
  if (/^https?:\/\//.test(part)) return <a key={i} href={part} target="_blank" rel="noopener" className="text-amber-500 hover:underline break-all">{part}</a>;
  if (/^\/[a-z0-9-]+$/.test(part)) return <Link key={i} to={part} className="text-amber-500 hover:underline">{`novemberkasan.se${part}`}</Link>;
  return part;
});

export const NewsCard = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <article
        data-testid={`news-card-${item.id}`}
        onClick={() => setOpen(true)}
        className="group cursor-pointer relative overflow-hidden rounded-xl bg-[#14161A] border border-white/8 hover-lift hover:border-amber-500/50"
      >
        <div className="relative aspect-[16/10] overflow-hidden bg-black">
          {item.image ? (
            <img src={resolveUrl(item.image)} alt={item.title} loading="lazy" className="h-full w-full object-contain opacity-90" />
          ) : (
            <div className="h-full w-full grid place-items-center text-white/20"><FileText className="w-10 h-10" /></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#14161A] via-transparent to-transparent" />
          <div className="absolute top-3 left-3 flex gap-2">
            {item.status && <StatusBadge status={item.status} />}
          </div>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-3 text-xs font-mono text-white/40 mb-2">
            <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{item.date || "—"}</span>
            {item.tag && <span className="text-amber-500">{item.tag}</span>}
          </div>
          <h3 className="font-display text-2xl font-bold uppercase text-white leading-tight group-hover:text-amber-500 transition-colors">
            {item.title}
          </h3>
          <p className="mt-2 text-sm text-white/60 line-clamp-2">{item.excerpt}</p>
          <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-amber-500">
            Läs mer <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </span>
        </div>
      </article>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-[#0F1114] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="news-modal">
          {item.image && <img src={resolveUrl(item.image)} alt={item.title} data-testid="news-modal-image" className="w-full h-auto max-h-[70vh] object-contain rounded-lg bg-black" />}
          <DialogHeader>
            <div className="flex items-center gap-3 text-xs font-mono text-white/50">
              <span>{item.date}</span>{item.status && <StatusBadge status={item.status} />}
            </div>
            <DialogTitle className="font-display text-3xl font-black uppercase text-white text-left">{item.title}</DialogTitle>
          </DialogHeader>
          <p className="text-white/80 leading-relaxed whitespace-pre-line">{linkify(item.body || item.excerpt)}</p>
          <div className="flex flex-wrap gap-3 pt-2">
            {item.link && (
              <a href={item.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-amber-500 font-semibold hover:underline">
                <ExternalLink className="w-4 h-4" /> Extern länk
              </a>
            )}
            {item.file_url && (
              <a href={resolveUrl(item.file_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-amber-500 font-semibold hover:underline">
                <FileText className="w-4 h-4" /> Dokument
              </a>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
